import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckIcon, ArrowLeftIcon, MapPinIcon, VideoCameraIcon, ComputerDesktopIcon, AcademicCapIcon } from '@heroicons/react/24/outline';
import api from '../../utils/api';

const C = {
  teal:'#00B4D8', navy:'#0D1B2A', success:'#10B981',
  border:'#E5E7EB', bg:'#F0F7FA', grey:'#6B7280', dark:'#1F2937',
  purple:'#8B5CF6', orange:'#F59E0B', red:'#EF4444', blue:'#3B82F6',
  indigo:'#6366F1', pink:'#EC4899', cyan:'#06B6D4',
};

// ─── TIER 1: AI Only ─────────────────────────────────────────────────────────
// No human tutor. Supervised AI practice only. Sell as upfront access period.
const AI_PROGRAMMES = [
  {
    id: 'ai_basic',
    tier: 'ai',
    name: 'AI Practice Only',
    subtitle: 'Structured AI practice — no tutor required',
    icon: '🤖',
    price: 39,
    upfrontLabel: '$398 for 12 months',
    accessPeriod: '12 months',
    color: C.teal,
    badge: null,
    features: [
      'Up to 1.5 hours of supervised AI practice per day',
      'All 7 skills: Grammar · Vocabulary · Reading · Writing · Conversation · IELTS · Business',
      'CEFR level assessment + progress tracking',
      'Personal vocabulary bank — builds as you practise',
      'Level certificates when you complete each stage',
      'Weekly progress report',
    ],
    important: 'No scheduled classes with a human tutor. AI practice only.',
    bestFor: 'Students who want affordable, structured daily practice and are happy to self-pace without a tutor.',
    weekSummary: 'Drop in any day: up to 1.5h supervised AI practice · Work through all 7 skill areas at your own pace',
  },
  {
    id: 'ai_ielts',
    tier: 'ai',
    name: 'AI IELTS Prep',
    subtitle: 'Intensive IELTS practice without a tutor',
    icon: '🎯',
    price: 69,
    upfrontLabel: '$352 for 6 months',
    accessPeriod: '6 months',
    color: C.blue,
    badge: null,
    features: [
      'Up to 1.5 hours of IELTS-focused AI practice per day',
      'Writing tasks graded on official IELTS band descriptors',
      'All 4 IELTS skills covered daily',
      'Timed reading practice — real exam simulation',
      'Full mock tests with band score estimates',
      '8–12 week structured prep plan',
      'IELTS Preparation Certificate on completion',
    ],
    important: 'No IELTS specialist tutor included. For tutor-led IELTS prep, see Online IELTS Group ($199/mo) or IELTS Group Intensive ($249/mo).',
    bestFor: 'Students preparing for IELTS who want intensive daily AI practice. Target: 5.5 to 7.0+',
    weekSummary: 'Daily: up to 1.5h IELTS AI practice · Weekly: full mock test with band score estimate',
  },
  {
    id: 'ai_business',
    tier: 'ai',
    name: 'AI Business English',
    subtitle: 'Professional English AI practice',
    icon: '💼',
    price: 69,
    upfrontLabel: '$352 for 6 months',
    accessPeriod: '6 months',
    color: C.grey,
    badge: null,
    features: [
      'Up to 1.5 hours of Business English AI practice per day',
      'Professional email writing with AI feedback',
      'Meeting language, presentations and negotiations',
      'Business vocabulary by industry sector',
      'Corporate communication scenarios',
      'Report and proposal writing practice',
      'Progress tracking and completion certificates',
    ],
    important: 'No Business English specialist tutor included. For tutor-led classes, see Corporate Group ($299/mo).',
    bestFor: 'Working professionals who want structured business English practice at their own pace.',
    weekSummary: 'Drop in any day: up to 1.5h Business English AI practice · Work on emails, presentations, vocabulary',
  },
];

// ─── TIER 2: Online Tutor ─────────────────────────────────────────────────────
// Live video sessions with a real tutor. For students abroad or who cannot attend in person.
// Same CEFR roadmap + course engine as physical. Pay per level (monthly or upfront).
const ONLINE_PROGRAMMES = [
  {
    id: 'online_group',
    tier: 'online',
    name: 'Online Group Classes',
    subtitle: 'Live group lessons via video call',
    icon: '🖥️',
    price: 59,
    color: C.cyan,
    badge: null,
    sessionsPerMonth: 12,
    sessionsPerWeek: 3,
    groupSize: 'up to 12 students',
    features: [
      '12 × live group video sessions per month (90 minutes each, 3× per week)',
      'Up to 12 students per group — CEFR level matched',
      'Taught by a qualified tutor via video call (Zoom / Google Meet)',
      'CEFR level tracking — structured roadmap from A1 to C2',
      'AI practice platform included between sessions',
      'Level exams and certificates at each stage',
      'Pay per level — monthly or upfront (10% off)',
    ],
    important: null,
    bestFor: 'Students who live abroad or cannot attend in person, and want regular structured lessons with a qualified tutor and other learners.',
    weekSummary: '3× per week: 90-min live group video session · Between sessions: AI practice on the platform',
  },
  {
    id: 'online_standard',
    tier: 'online',
    name: 'Online Standard',
    subtitle: 'Group video classes + daily AI practice',
    icon: '⭐',
    price: 85,
    color: C.indigo,
    badge: 'Most Popular Online',
    sessionsPerMonth: 12,
    sessionsPerWeek: 3,
    groupSize: 'up to 12 students',
    features: [
      '12 × live group video sessions per month (90 minutes each, 3× per week)',
      'Up to 12 students per group — CEFR level matched',
      'Daily AI practice platform — all 7 skill types',
      'Tutor reviews your AI progress before every class',
      'Personal vocabulary bank and progress tracking',
      'CEFR assessments and level certificates',
      'Pay per level — monthly or upfront (10% off)',
    ],
    important: null,
    bestFor: 'Students abroad who want the full school experience — regular live tutor sessions combined with daily AI practice.',
    weekSummary: '3× per week: 90-min live group video session · Daily: AI practice platform (all 7 skill types)',
  },
  {
    id: 'online_1on1',
    tier: 'online',
    name: 'Online Premium Group',
    subtitle: 'Premium group video classes with AI coaching',
    icon: '💎',
    price: 129,
    color: C.purple,
    badge: null,
    sessionsPerMonth: 12,
    sessionsPerWeek: 3,
    groupSize: 'up to 12 students',
    features: [
      '12 × premium group video sessions per month (90 minutes each, 3× per week)',
      'Up to 12 students per group — CEFR level matched',
      'Tutor tailors every session to the group\'s pace and goals',
      'Daily AI practice platform included',
      'Tutor reviews full group AI progress before each class',
      'CEFR assessments, certificates and full roadmap',
      'Pay per level — monthly or upfront (10% off)',
    ],
    important: null,
    bestFor: 'Students abroad who want premium group tuition with close AI-backed progress tracking between every session.',
    weekSummary: '3× per week: 90-min premium group video session · Daily: AI practice platform',
  },
  {
    id: 'online_ielts',
    tier: 'online',
    name: 'Online IELTS Group',
    subtitle: 'Fast-track IELTS prep via group video',
    icon: '🏆',
    price: 199,
    color: C.red,
    badge: 'Best for IELTS',
    sessionsPerMonth: 20,
    sessionsPerWeek: 5,
    groupSize: 'up to 12 students',
    features: [
      '20 × live IELTS group sessions per month (90 minutes each, 5× per week)',
      'Up to 12 students per group — IELTS band matched',
      'Taught by an IELTS-qualified specialist via video call',
      'Writing tasks reviewed and graded every class',
      'Speaking practice with detailed feedback each session',
      'Full timed mock test every 2 weeks',
      'Band score tracking week by week',
      'Pay per level — monthly or upfront (10% off)',
    ],
    important: null,
    bestFor: 'Students abroad with an urgent IELTS target — university admission, visa or professional certification. Target: 6.0 / 6.5 / 7.0 / 7.5',
    weekSummary: '5× per week: 90-min live IELTS group video session · Daily: intensive IELTS AI practice',
  },
];

// ─── TIER 3: Physical Centre ──────────────────────────────────────────────────
// Attend the learning centre in person. Same CEFR engine. Pay per level.
const PHYSICAL_PROGRAMMES = [
  {
    id: 'starter_group',
    tier: 'physical',
    name: 'Group Classes',
    subtitle: 'Learn together at your level',
    icon: '👥',
    price: 59,
    color: C.success,
    badge: null,
    sessionsPerMonth: 12,
    sessionsPerWeek: 3,
    groupSize: 'up to 12 students',
    features: [
      '12 × group classes per month at your centre (90 minutes each, 3× per week)',
      'Up to 12 students per group — all at the same CEFR level',
      'Classes taught by a qualified tutor',
      'AI Practice platform available between classes',
      'Tutor reviews your AI progress before every class',
      'CEFR tracking, certificates and full learning roadmap',
      'Pay per level — monthly or upfront (10% off)',
    ],
    important: null,
    bestFor: 'Students starting their English journey who want the energy and motivation of learning with others at the same stage.',
    weekSummary: '3× per week: 90-min group class at the centre · Between classes: AI practice platform',
  },
  {
    id: 'standard',
    tier: 'physical',
    name: 'Standard',
    subtitle: 'The complete school experience',
    icon: '⭐',
    price: 99,
    color: C.navy,
    badge: 'Most Popular',
    sessionsPerMonth: 12,
    sessionsPerWeek: 3,
    groupSize: 'up to 12 students',
    features: [
      '12 × group classes per month at your centre (90 minutes each, 3× per week)',
      'Up to 12 students per group — CEFR level matched',
      'Up to 2 hours of supervised AI practice per day at the centre',
      'Tutor sees your full AI history — every class is personalised to exactly what you need',
      'All 7 AI practice types included',
      'Personal vocabulary bank and weekly progress report',
      'CEFR assessments, certificates and full roadmap',
      'Pay per level — monthly or upfront (10% off)',
    ],
    important: null,
    bestFor: 'Students who want the full school experience — structured daily AI practice at the centre combined with regular expert tutor classes.',
    weekSummary: '3× per week: 90-min group class with your tutor · Daily: up to 2h supervised AI practice at the centre',
  },
  {
    id: 'premium',
    tier: 'physical',
    name: 'Premium Group',
    subtitle: 'Premium group coaching for fastest results',
    icon: '💎',
    price: 159,
    color: C.purple,
    badge: null,
    sessionsPerMonth: 12,
    sessionsPerWeek: 3,
    groupSize: 'up to 12 students',
    features: [
      '12 × premium group sessions per month at your centre (90 minutes each, 3× per week)',
      'Up to 12 students per group — CEFR level matched',
      'Tutor tailors every class to the group\'s pace and goals',
      'Up to 2 hours of supervised AI practice per day',
      'Tutor reviews all AI practice before each class',
      'CEFR assessments, certificates and full roadmap',
      'Pay per level — monthly or upfront (10% off)',
    ],
    important: null,
    bestFor: 'Students who want premium group classes with close AI-backed progress monitoring and a tutor who knows every student\'s needs.',
    weekSummary: '3× per week: 90-min premium group session at the centre · Daily: up to 2h supervised AI practice',
  },
  {
    id: 'ielts_intensive',
    tier: 'physical',
    name: 'IELTS Group Intensive',
    subtitle: 'Fastest route to your target band score',
    icon: '🏆',
    price: 249,
    color: C.red,
    badge: 'Best for IELTS',
    sessionsPerMonth: 20,
    sessionsPerWeek: 5,
    groupSize: 'up to 12 students',
    features: [
      '20 × IELTS group sessions per month at your centre (90 minutes each, 5× per week)',
      'Up to 12 students per group — IELTS band matched',
      'Taught by an IELTS-qualified specialist tutor',
      'Writing tasks reviewed and graded in every class',
      'Speaking practice with detailed feedback each session',
      'Full timed mock test every 2 weeks',
      'Band score tracking — see improvement week by week',
      'Pay per level — monthly or upfront (10% off)',
    ],
    important: null,
    bestFor: 'Students with a specific IELTS target for university admission, visa applications or professional certification. Target band: 6.0 / 6.5 / 7.0 / 7.5',
    weekSummary: '5× per week: 90-min IELTS group session at the centre · Daily: up to 2h IELTS AI practice',
  },
  {
    id: 'corporate',
    tier: 'physical',
    name: 'Corporate Group',
    subtitle: 'Executive English for professionals',
    icon: '🏢',
    price: 299,
    color: C.orange,
    badge: null,
    sessionsPerMonth: 12,
    sessionsPerWeek: 3,
    groupSize: 'up to 12 students',
    features: [
      '12 × Business English group sessions per month (90 minutes each, 3× per week)',
      'Up to 12 students per group — industry and level matched',
      'Classes built around your sector, role and goals',
      'Presentation coaching, business writing and negotiations',
      'Meeting language, emails, reports and proposals',
      'Up to 2 hours of Business English AI practice per day',
      'Corporate group completion certificate',
    ],
    important: null,
    bestFor: 'Professionals, managers and executives who need English at boardroom level. Learn alongside peers from similar industries.',
    weekSummary: '3× per week: 90-min Business English group session · Daily: up to 2h Business English AI practice',
  },
];

const ALL_PROGRAMMES = [...AI_PROGRAMMES, ...ONLINE_PROGRAMMES, ...PHYSICAL_PROGRAMMES];

// ─── Tier meta ────────────────────────────────────────────────────────────────
const TIERS = [
  {
    key: 'ai',
    icon: '🤖',
    label: 'AI Practice Only',
    sublabel: 'Self-paced · No human tutor · Supervised at your centre',
    desc: 'Structured AI practice sessions at your learning centre. No scheduled tutor classes — you work through the platform at your own pace. Sold as a fixed access period, not monthly.',
    deliveryIcon: <ComputerDesktopIcon style={{ width:15, height:15 }} />,
    deliveryText: 'AI platform — supervised at centre',
    priceNote: 'Sold upfront — fixed access period',
    programmes: AI_PROGRAMMES,
  },
  {
    key: 'online',
    icon: '🖥️',
    label: 'Online Tutor Classes',
    sublabel: 'Live video · Qualified tutors · For students abroad or remote',
    desc: 'Live 90-minute sessions with a qualified tutor over video call (Zoom / Google Meet). Same CEFR roadmap and course engine as physical attendance — you just study from wherever you are. Pay per CEFR level.',
    deliveryIcon: <VideoCameraIcon style={{ width:15, height:15 }} />,
    deliveryText: 'Live video call (Zoom / Google Meet)',
    priceNote: 'Pay per level · Monthly or upfront (10% off)',
    programmes: ONLINE_PROGRAMMES,
  },
  {
    key: 'physical',
    icon: '🏫',
    label: 'Physical Centre Classes',
    sublabel: 'Attend in person · Qualified tutors · AI practice on-site',
    desc: 'Attend your learning centre in person. All classes are taught by qualified tutors. AI practice sessions take place at the centre between classes. Pay per CEFR level.',
    deliveryIcon: <MapPinIcon style={{ width:15, height:15 }} />,
    deliveryText: 'In person at your learning centre',
    priceNote: 'Pay per level · Monthly or upfront (10% off)',
    programmes: PHYSICAL_PROGRAMMES,
  },
];

// ─── Fee breakdown helper ────────────────────────────────────────────────────
// Shows what a student actually pays (level fee not monthly)
const LEVEL_DURATIONS = { A1:3.1, A2:4.1, B1:5.1, B2:6.2, C1:7.2 };

function FeeBreakdown({ prog }) {
  if (prog.tier === 'ai') {
    return (
      <div style={{ background:'#F0FDF4', borderRadius:10, padding:12, marginBottom:16, fontSize:12 }}>
        <div style={{ fontWeight:700, color:'#065F46', marginBottom:4 }}>💳 How pricing works</div>
        <div style={{ color:'#047857', lineHeight:1.6 }}>
          Sold as a single upfront payment for {prog.accessPeriod} of access.<br/>
          <strong>{prog.upfrontLabel}</strong> (save 10% vs paying monthly)
        </div>
      </div>
    );
  }
  const mo = prog.price;
  const a1months = LEVEL_DURATIONS['A1'];
  const a1total = Math.round(mo * a1months);
  const a1upfront = Math.round(a1total * 0.9);
  return (
    <div style={{ background:'#EFF6FF', borderRadius:10, padding:12, marginBottom:16, fontSize:12 }}>
      <div style={{ fontWeight:700, color:'#1E40AF', marginBottom:6 }}>💳 How pricing works — pay per CEFR level</div>
      <div style={{ color:'#1D4ED8', lineHeight:1.8 }}>
        You pay for one level at a time — not forever upfront.<br/>
        <strong>Example — A1 level (~{a1months} months at {prog.sessionsPerWeek}×/week):</strong><br/>
        Monthly: ${mo}/mo × {a1months}mo = <strong>${a1total}</strong> total<br/>
        Upfront: <strong>${a1upfront}</strong> (10% off — save ${a1total - a1upfront})<br/>
        When you pass the exam, you pay for the next level.
      </div>
    </div>
  );
}

export default function SubscribePage() {
  const navigate      = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentPlan, setCurrentPlan] = useState(null);
  const [selected,    setSelected]    = useState(null);
  const [step,        setStep]        = useState('plans');
  const [loading,     setLoading]     = useState(false);
  const [bankInfo,    setBankInfo]    = useState('');
  const [payInfo,     setPayInfo]     = useState(null);   // structured bank details
  const [proofState,  setProofState]  = useState('');     // '' | 'uploading' | 'done' | 'error'
  const [promoCode,   setPromoCode]   = useState('');
  const [promoResult, setPromoResult] = useState(null);
  const [promoLoading,setPromoLoading]= useState(false);
  const [promoError,  setPromoError]  = useState('');
  const [tenantName,  setTenantName]  = useState('your centre');

  useEffect(() => {
    api.get('/api/student/me').then(r => {
      setCurrentPlan(r.data.student?.package || null);
      if (r.data.tenant?.name) setTenantName(r.data.tenant.name);
    }).catch(() => {});
    api.get('/api/student/plans').then(r => {
      if (r.data.bank_transfer_instructions) setBankInfo(r.data.bank_transfer_instructions);
      if (r.data.tenant_name) setTenantName(r.data.tenant_name);
    }).catch(() => {});
    api.get('/api/student/payment-info').then(r => setPayInfo(r.data)).catch(() => {});
  }, []);

  const handleProofUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setProofState('uploading');
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post('/api/student/upload-payment-proof', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setProofState('done');
    } catch {
      setProofState('error');
    }
  };

  // Deep-link: /programmes?recommended=<name|id> opens that programme's detail
  // directly (used by the assessment "Enrol" CTA so students land on their
  // recommended course ready to enrol).
  useEffect(() => {
    const rec = (searchParams.get('recommended') || '').trim().toLowerCase();
    if (!rec) return;
    const norm = s => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const target = norm(rec);
    const match = ALL_PROGRAMMES.find(p => norm(p.id) === target || norm(p.name) === target)
      || ALL_PROGRAMMES.find(p => norm(p.name).includes(target) || target.includes(norm(p.name)));
    if (match) { setSelected(match); setStep('detail'); }
  }, [searchParams]);

  const handleEnrol = async () => {
    if (!selected) return;
    setLoading(true);
    try {
      await api.post('/api/student/subscribe', { plan_id: selected.id, promo_code: promoCode.trim() || null });
      setStep('done');
    } catch (err) {
      alert(err.response?.data?.error || 'Enrolment failed. Please contact your school or try again.');
    }
    setLoading(false);
  };

  const applyPromo = async () => {
    if (!promoCode.trim() || !selected) return;
    setPromoLoading(true); setPromoError(''); setPromoResult(null);
    try {
      const r = await api.post('/api/promotions/validate', { code: promoCode.trim(), plan_id: selected.id });
      setPromoResult(r.data);
    } catch (err) {
      setPromoError(err.response?.data?.error || 'Invalid or expired code');
    }
    setPromoLoading(false);
  };

  const clearPromo = () => { setPromoCode(''); setPromoResult(null); setPromoError(''); };

  // ── Done ──────────────────────────────────────────────────────────────────
  if (step === 'done') return (
    <div style={{ minHeight:'100vh', background:C.bg, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <motion.div initial={{ scale:0.85, opacity:0 }} animate={{ scale:1, opacity:1 }}
        style={{ background:'#fff', borderRadius:20, padding:48, textAlign:'center', maxWidth:500, boxShadow:'0 4px 28px rgba(0,0,0,0.09)' }}>
        <div style={{ fontSize:60, marginBottom:12 }}>🎉</div>
        <h2 style={{ margin:'0 0 8px', fontWeight:800, color:C.dark }}>Place Confirmed!</h2>
        <p style={{ color:C.grey, marginBottom:6 }}>
          You are enrolled on <strong>{selected?.name}</strong> at <strong>{tenantName}</strong>.
        </p>
        <div style={{ background:'#EFF6FF', borderRadius:12, padding:14, marginBottom:20, fontSize:13, color:'#1E40AF', textAlign:'left' }}>
          <strong>What happens next:</strong><br/>
          Your school will confirm your enrolment and send you payment details.<br/>
          {selected?.tier === 'online' ? 'You will receive a tutor assignment and your first video session link.' : ''}<br/>
          {selected?.tier === 'ai' ? 'You will receive access to the AI practice platform.' : ''}<br/>
          {selected?.tier === 'physical' ? 'You will receive your class schedule and centre access details.' : ''}
        </div>
        {/* Local card payment — shown first, most prominent */}
        {payInfo?.bank_details?.local_payment && (
          <div style={{ background:'#ECFDF5', border:'2px solid #10B981', borderRadius:12, padding:16, marginBottom:12, textAlign:'left' }}>
            <div style={{ fontWeight:800, fontSize:14, color:'#065F46', marginBottom:6 }}>💳 Pay by local card — fastest</div>
            <div style={{ fontSize:14, color:C.dark, fontWeight:600, lineHeight:1.8 }}>{payInfo.bank_details.local_payment}</div>
          </div>
        )}
        {/* Bank details — same as the dashboard Course Payment section */}
        {(payInfo?.bank_details?.account_number || bankInfo) && (
          <div style={{ background:C.bg, borderRadius:12, padding:16, marginBottom:16, textAlign:'left', fontSize:13 }}>
            <div style={{ fontWeight:700, marginBottom:8 }}>💳 Pay to enrol</div>
            {payInfo?.bank_details?.account_number ? (
              <div style={{ display:'flex', flexDirection:'column', gap:4, color:C.dark }}>
                {payInfo.bank_details.bank_name && <div><b>Bank Name:</b> {payInfo.bank_details.bank_name}</div>}
                {payInfo.bank_details.account_holder && <div><b>Account Holder:</b> {payInfo.bank_details.account_holder}</div>}
                {payInfo.bank_details.account_number && <div><b>Account Number:</b> {payInfo.bank_details.account_number}</div>}
                {payInfo.bank_details.sort_code && <div><b>Sort Code:</b> {payInfo.bank_details.sort_code}</div>}
                {payInfo.bank_details.swift_bic && <div><b>SWIFT / BIC:</b> {payInfo.bank_details.swift_bic}</div>}
                {payInfo.bank_details.payment_notes && <div style={{ marginTop:6, color:C.grey }}>{payInfo.bank_details.payment_notes}</div>}
                <div style={{ marginTop:6, color:C.grey }}>Please use your full name as the payment reference.</div>
              </div>
            ) : (
              <pre style={{ whiteSpace:'pre-wrap', margin:0, fontSize:12, color:C.grey }}>{bankInfo}</pre>
            )}
          </div>
        )}

        {/* Upload payment proof — same flow as the dashboard */}
        <div style={{ background:'#fff', border:`1px solid ${C.border}`, borderRadius:12, padding:16, marginBottom:20, textAlign:'left' }}>
          <div style={{ fontWeight:700, marginBottom:8, fontSize:13 }}>📎 Upload payment proof</div>
          {proofState === 'done' ? (
            <div style={{ fontSize:13, color:C.success, fontWeight:600 }}>✅ Proof uploaded — your school will verify it and unlock your course.</div>
          ) : (
            <>
              <label style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'9px 16px', borderRadius:8, background:C.teal, color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer', opacity: proofState==='uploading' ? 0.7 : 1 }}>
                {proofState === 'uploading' ? 'Uploading…' : 'Choose file & upload'}
                <input type="file" accept="image/png,image/jpeg,image/webp,application/pdf" onChange={handleProofUpload} disabled={proofState==='uploading'} style={{ display:'none' }} />
              </label>
              {proofState === 'error' && <div style={{ fontSize:12, color:'#EF4444', marginTop:8 }}>Upload failed — please try again.</div>}
              <div style={{ fontSize:12, color:C.grey, marginTop:8 }}>After you pay, upload your receipt/screenshot here. Once your school verifies it, your course unlocks.</div>
            </>
          )}
        </div>

        <button onClick={() => navigate('/dashboard')}
          style={{ width:'100%', padding:14, background:C.teal, color:'#fff', border:'none', borderRadius:12, fontWeight:700, fontSize:15, cursor:'pointer' }}>
          Go to Your Learning Portal →
        </button>
      </motion.div>
    </div>
  );

  // ── Detail view ───────────────────────────────────────────────────────────
  if (step === 'detail' && selected) {
    const tier = TIERS.find(t => t.key === selected.tier);
    return (
      <div style={{ minHeight:'100vh', background:C.bg, padding:'20px 0 40px' }}>
        <div style={{ maxWidth:680, margin:'0 auto', padding:'0 20px' }}>
          <button onClick={() => { setStep('plans'); clearPromo(); }}
            style={{ background:'none', border:'none', color:C.grey, cursor:'pointer', display:'flex', alignItems:'center', gap:6, marginBottom:24, fontSize:14 }}>
            <ArrowLeftIcon style={{ width:15, height:15 }} /> Back to all programmes
          </button>

          <motion.div initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }}
            style={{ background:'#fff', borderRadius:20, overflow:'hidden', boxShadow:'0 4px 24px rgba(0,0,0,0.08)' }}>

            {/* Header */}
            <div style={{ background:selected.color, padding:'32px 36px', color:'#fff' }}>
              <div style={{ fontSize:48, marginBottom:8 }}>{selected.icon}</div>
              {selected.badge && (
                <span style={{ background:'rgba(255,255,255,0.25)', padding:'3px 12px', borderRadius:20, fontSize:12, fontWeight:700, marginBottom:12, display:'inline-block' }}>
                  {selected.badge}
                </span>
              )}
              <h2 style={{ margin:'8px 0 4px', fontWeight:800, fontSize:24 }}>{selected.name}</h2>
              <p style={{ margin:0, opacity:0.85, fontSize:15 }}>{selected.subtitle}</p>
              <div style={{ marginTop:16 }}>
                {promoResult ? (
                  <div style={{ display:'flex', alignItems:'baseline', gap:8 }}>
                    <span style={{ fontSize:20, opacity:0.6, textDecoration:'line-through', fontWeight:400 }}>${selected.price}</span>
                    <span style={{ fontSize:32, fontWeight:800 }}>${promoResult.final_price}</span>
                    <span style={{ fontSize:14, opacity:0.7, fontWeight:400 }}>/month</span>
                  </div>
                ) : (
                  <div style={{ fontSize:32, fontWeight:800 }}>
                    ${selected.price}<span style={{ fontSize:14, opacity:0.7, fontWeight:400 }}>/month</span>
                  </div>
                )}
              </div>
            </div>

            {/* Delivery method */}
            <div style={{
              background: selected.tier==='ai' ? '#F0FDF4' : selected.tier==='online' ? '#EFF6FF' : '#F0FDF4',
              padding:'13px 28px', display:'flex', alignItems:'center', gap:10, borderBottom:`1px solid ${C.border}`
            }}>
              <span style={{ color: selected.tier==='online' ? C.blue : C.success }}>
                {tier?.deliveryIcon}
              </span>
              <span style={{ fontSize:13, fontWeight:600, color: selected.tier==='online' ? '#1E40AF' : '#065F46' }}>
                {tier?.deliveryText}
                {selected.groupSize ? ` · ${selected.groupSize}` : ''}
              </span>
            </div>

            <div style={{ padding:'28px 36px' }}>
              <FeeBreakdown prog={selected} />

              <h3 style={{ margin:'0 0 14px', color:C.dark, fontSize:15, fontWeight:700 }}>What's included:</h3>
              <div style={{ marginBottom:24 }}>
                {selected.features.map(f => (
                  <div key={f} style={{ display:'flex', gap:10, alignItems:'flex-start', marginBottom:10 }}>
                    <CheckIcon style={{ width:16, height:16, color:C.success, flexShrink:0, marginTop:2 }} />
                    <span style={{ fontSize:14, color:C.dark, lineHeight:1.5 }}>{f}</span>
                  </div>
                ))}
              </div>

              {selected.weekSummary && (
                <div style={{ background:C.bg, borderRadius:12, padding:16, marginBottom:20 }}>
                  <div style={{ fontWeight:700, fontSize:13, color:C.dark, marginBottom:8 }}>📅 A typical week:</div>
                  <div style={{ fontSize:13, color:C.grey, lineHeight:1.7 }}>{selected.weekSummary}</div>
                </div>
              )}

              {selected.important && (
                <div style={{ background:'#FEF9C3', borderRadius:12, padding:14, marginBottom:20, border:'1px solid #FDE68A' }}>
                  <div style={{ fontWeight:700, fontSize:13, color:'#92400E', marginBottom:4 }}>ℹ️ Important</div>
                  <div style={{ fontSize:13, color:'#92400E' }}>{selected.important}</div>
                </div>
              )}

              <div style={{ background:'#EFF6FF', borderRadius:12, padding:14, marginBottom:24 }}>
                <div style={{ fontWeight:700, fontSize:13, color:'#1E40AF', marginBottom:4 }}>✅ Best for</div>
                <div style={{ fontSize:13, color:'#1E40AF' }}>{selected.bestFor}</div>
              </div>

              {/* ── Promo Code ───────────────────────────────────────────── */}
              <div style={{ marginBottom:20 }}>
                <div style={{ fontWeight:700, fontSize:13, color:C.dark, marginBottom:8 }}>🏷️ Have a promo code?</div>
                <div style={{ display:'flex', gap:8 }}>
                  <input
                    value={promoCode}
                    onChange={e => { setPromoCode(e.target.value.toUpperCase()); setPromoResult(null); setPromoError(''); }}
                    onKeyDown={e => e.key === 'Enter' && applyPromo()}
                    placeholder="Enter code (optional)"
                    style={{ flex:1, padding:'10px 14px', border:'1.5px solid '+(promoResult ? '#10B981' : promoError ? '#EF4444' : C.border), borderRadius:10, fontSize:14, outline:'none', background:'#fff' }}
                  />
                  <button onClick={applyPromo} disabled={promoLoading || !promoCode.trim()}
                    style={{ padding:'10px 18px', background:promoCode.trim() ? C.teal : '#E5E7EB', color:promoCode.trim() ? '#fff' : C.grey, border:'none', borderRadius:10, fontWeight:700, fontSize:13, cursor:promoCode.trim() ? 'pointer' : 'default', whiteSpace:'nowrap' }}>
                    {promoLoading ? '…' : 'Apply'}
                  </button>
                </div>
                {promoError && <div style={{ color:'#EF4444', fontSize:12, marginTop:6 }}>{promoError}</div>}
                {promoResult && (
                  <div style={{ background:'#D1FAE5', borderRadius:10, padding:'10px 14px', marginTop:8, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <div>
                      <div style={{ fontWeight:700, fontSize:13, color:'#065F46' }}>✅ {promoResult.promotion_name}</div>
                      <div style={{ fontSize:12, color:'#065F46' }}>{promoResult.discount_percent}% discount applied</div>
                    </div>
                    <div style={{ fontSize:20, fontWeight:800, color:'#065F46' }}>${promoResult.final_price}<span style={{ fontSize:12, fontWeight:400 }}>/mo</span></div>
                  </div>
                )}
              </div>

              <button onClick={handleEnrol} disabled={loading || currentPlan === selected.id}
                style={{
                  width:'100%', padding:'15px 0',
                  background: currentPlan === selected.id ? '#E5E7EB' : selected.color,
                  color: currentPlan === selected.id ? C.grey : '#fff',
                  border:'none', borderRadius:12, fontWeight:700, fontSize:16,
                  cursor: (loading || currentPlan === selected.id) ? 'not-allowed' : 'pointer',
                }}>
                {loading ? 'Confirming…' : currentPlan === selected.id ? '✅ Your current programme' : `Enrol on ${selected.name} →`}
              </button>
              <p style={{ textAlign:'center', fontSize:12, color:C.grey, marginTop:10 }}>
                Your school will confirm enrolment and send payment details
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // ── Programme list ────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight:'100vh', background:C.bg }}>
      {/* Header */}
      <div style={{ background:C.navy, padding:'24px 28px' }}>
        <button onClick={() => navigate('/dashboard')}
          style={{ background:'rgba(255,255,255,0.1)', border:'none', color:'#fff', padding:'8px 14px', borderRadius:8, cursor:'pointer', display:'flex', alignItems:'center', gap:6, fontSize:13, marginBottom:16 }}>
          <ArrowLeftIcon style={{ width:14, height:14 }} /> Back to Learning Portal
        </button>
        <div style={{ color:'#fff', fontWeight:800, fontSize:22 }}>Choose Your Learning Programme</div>
        <div style={{ color:'rgba(255,255,255,0.65)', fontSize:14, marginTop:4 }}>
          All programmes follow the CEFR framework — A1 through C1. You pay one level at a time and progress at your own speed.
        </div>
      </div>

      {/* How it works bar */}
      <div style={{ background:'#fff', borderBottom:`1px solid ${C.border}`, padding:'14px 28px' }}>
        <div style={{ maxWidth:1100, margin:'0 auto', display:'flex', flexWrap:'wrap', gap:28, justifyContent:'center' }}>
          {[
            { icon:'📋', text:'Free placement test — find your CEFR level' },
            { icon:'📚', text:'Pay one level at a time — monthly or upfront' },
            { icon:'✅', text:'Pass the level exam → move to the next level' },
            { icon:'🎓', text:'Certificate at each stage — A1 through C1' },
          ].map(({ icon, text }) => (
            <div key={text} style={{ display:'flex', alignItems:'center', gap:8, color:C.dark, fontSize:13, fontWeight:600 }}>
              <span>{icon}</span>{text}
            </div>
          ))}
        </div>
      </div>

      <div style={{ maxWidth:1100, margin:'0 auto', padding:'28px 20px 40px' }}>
        {currentPlan && (
          <div style={{ textAlign:'center', marginBottom:24 }}>
            <span style={{ background:'#D1FAE5', color:'#065F46', padding:'6px 18px', borderRadius:20, fontWeight:600, fontSize:13 }}>
              ✅ Current programme: {ALL_PROGRAMMES.find(p => p.id === currentPlan)?.name || currentPlan}
            </span>
          </div>
        )}

        {/* Three tiers */}
        {TIERS.map((tier, ti) => (
          <div key={tier.key} style={{ marginBottom:52 }}>
            {/* Tier header */}
            <div style={{
              display:'flex', alignItems:'flex-start', gap:16,
              background:'#fff', borderRadius:14, padding:'18px 24px',
              marginBottom:20, border:`1px solid ${C.border}`,
              boxShadow:'0 2px 8px rgba(0,0,0,0.04)',
            }}>
              <div style={{ fontSize:32, lineHeight:1 }}>{tier.icon}</div>
              <div style={{ flex:1 }}>
                <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                  <h2 style={{ margin:0, fontWeight:800, fontSize:18, color:C.dark }}>{tier.label}</h2>
                  <span style={{ background:C.bg, color:C.grey, fontSize:11, fontWeight:600, padding:'3px 10px', borderRadius:20, border:`1px solid ${C.border}` }}>
                    {tier.sublabel}
                  </span>
                </div>
                <p style={{ margin:'6px 0 0', color:C.grey, fontSize:13, lineHeight:1.6, maxWidth:640 }}>{tier.desc}</p>
                <div style={{ marginTop:8, display:'flex', alignItems:'center', gap:6 }}>
                  <span style={{ color:C.teal }}>{tier.deliveryIcon}</span>
                  <span style={{ fontSize:12, color:C.grey, fontWeight:600 }}>{tier.deliveryText}</span>
                  <span style={{ color:C.border, margin:'0 4px' }}>·</span>
                  <span style={{ fontSize:12, color:C.grey }}>{tier.priceNote}</span>
                </div>
              </div>
            </div>

            {/* Programme cards */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(270px, 1fr))', gap:16 }}>
              {tier.programmes.map((prog, i) => (
                <motion.div key={prog.id}
                  initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }} transition={{ delay:ti*0.1 + i*0.06 }}
                  whileHover={{ y:-4, boxShadow:'0 10px 32px rgba(0,0,0,0.11)' }}
                  style={{
                    background:'#fff', borderRadius:16, padding:22, cursor:'pointer',
                    border:`2px solid ${currentPlan === prog.id ? prog.color : prog.badge ? prog.color+'33' : C.border}`,
                    boxShadow:'0 2px 8px rgba(0,0,0,0.05)', transition:'all 0.2s',
                    position:'relative',
                  }}
                  onClick={() => { setSelected(prog); setStep('detail'); clearPromo(); }}>

                  {prog.badge && (
                    <div style={{ position:'absolute', top:-11, right:16, background:prog.color, color:'#fff', padding:'3px 12px', borderRadius:20, fontSize:11, fontWeight:700 }}>
                      {prog.badge}
                    </div>
                  )}
                  {currentPlan === prog.id && (
                    <div style={{ position:'absolute', top:-11, left:16, background:C.success, color:'#fff', padding:'3px 12px', borderRadius:20, fontSize:11, fontWeight:700 }}>
                      ✅ Current
                    </div>
                  )}

                  <div style={{ fontSize:32, marginBottom:8 }}>{prog.icon}</div>
                  <h3 style={{ margin:'0 0 2px', fontWeight:800, fontSize:16, color:C.dark }}>{prog.name}</h3>
                  <p style={{ margin:'0 0 10px', color:C.grey, fontSize:12, lineHeight:1.4 }}>{prog.subtitle}</p>

                  <div style={{ fontSize:26, fontWeight:800, color:prog.color, marginBottom:4 }}>
                    ${prog.price}<span style={{ fontSize:13, color:C.grey, fontWeight:400 }}>/mo</span>
                  </div>

                  {/* Session count pill */}
                  {prog.sessionsPerMonth && (
                    <div style={{ display:'flex', gap:6, marginBottom:12, flexWrap:'wrap' }}>
                      <span style={{ background:prog.color+'18', color:prog.color, fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:20 }}>
                        {prog.sessionsPerMonth} sessions/month
                      </span>
                      <span style={{ background:'#F3F4F6', color:C.grey, fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:20 }}>
                        {prog.sessionsPerWeek}× per week · 90 min
                      </span>
                    </div>
                  )}
                  {prog.tier === 'ai' && (
                    <div style={{ marginBottom:12 }}>
                      <span style={{ background:C.teal+'18', color:C.teal, fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:20 }}>
                        {prog.upfrontLabel}
                      </span>
                    </div>
                  )}

                  <div style={{ marginBottom:14 }}>
                    {prog.features.slice(0, 3).map(f => (
                      <div key={f} style={{ display:'flex', gap:7, alignItems:'flex-start', marginBottom:6 }}>
                        <CheckIcon style={{ width:13, height:13, color:C.success, flexShrink:0, marginTop:2 }} />
                        <span style={{ fontSize:12, color:C.dark, lineHeight:1.4 }}>{f}</span>
                      </div>
                    ))}
                    {prog.features.length > 3 && (
                      <div style={{ fontSize:12, color:C.grey, marginTop:4 }}>+ {prog.features.length - 3} more included</div>
                    )}
                  </div>

                  <button style={{
                    width:'100%', padding:'9px 0', borderRadius:10,
                    border:`1.5px solid ${prog.color}`, fontWeight:700, fontSize:13, cursor:'pointer',
                    background: currentPlan === prog.id ? prog.color : 'transparent',
                    color: currentPlan === prog.id ? '#fff' : prog.color,
                  }}>
                    {currentPlan === prog.id ? '✅ Current Programme' : 'View Programme →'}
                  </button>
                </motion.div>
              ))}
            </div>
          </div>
        ))}

        {/* Compare all */}
        <div style={{ background:'#fff', borderRadius:14, padding:'20px 24px', border:`1px solid ${C.border}`, textAlign:'center' }}>
          <div style={{ fontWeight:700, color:C.dark, marginBottom:6 }}>Not sure which to choose?</div>
          <div style={{ fontSize:13, color:C.grey, marginBottom:12 }}>
            Start with the free placement test to find your CEFR level — your school will recommend the right programme based on your goals, location and schedule.
          </div>
          <div style={{ fontSize:12, color:C.grey }}>
            💳 All physical & online tutor programmes: pay per CEFR level — monthly or 10% off upfront · 
            🤖 AI programmes: one upfront payment for full access period
          </div>
        </div>
      </div>
    </div>
  );
}
